import { clone } from './cloning.js';
import { noop } from './functions.js';
import { each } from './loops.js';
import { escapeRegExp } from './regexp.js';
import { isArray, isObject, isPlainObject } from './types.js';

/*-------------------
       Objects
--------------------*/

/*
  Return keys from object
*/
export const keys = (obj) => {
  if (isObject(obj)) {
    return Object.keys(obj);
  }
};

export const values = (obj) => {
  if (isObject(obj)) {
    return Object.values(obj);
  }
};

export const filterObject = (obj, callback) => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([key, value]) => callback(value, key)),
  );
};

export const mapObject = (obj, callback) => {
  return Object.fromEntries(
    Object.entries(obj)
      .map(([key, value]) => [key, callback(value, key)]),
  );
};

/*
  Handles properly copying getter/setters
*/
export const extend = (obj, ...sources) => {
  sources.forEach((source) => {
    let descriptor, prop;
    if (source) {
      for (prop in source) {
        descriptor = Object.getOwnPropertyDescriptor(source, prop);
        if (descriptor === undefined) {
          obj[prop] = source[prop];
        }
        else {
          Object.defineProperty(obj, prop, descriptor);
        }
      }
    }
  });
  return obj;
};

export const deepExtend = (target, ...args) => {
  if (!isObject(target)) {
    return target;
  }

  // Check if last argument is options object
  const lastArg = args[args.length - 1];
  const defaultOptions = { preserveNonCloneable: true, preserveDOM: true };
  const hasOptions = keys(defaultOptions).some(key => lastArg?.[key] !== undefined);
  const options = hasOptions ? { ...defaultOptions, ...lastArg } : defaultOptions;
  const sources = hasOptions ? args.slice(0, -1) : args;

  each(sources, (source) => {
    // Skip if source is not a plain object
    if (!isPlainObject(source)) {
      return;
    }

    each(source, (val, key) => {
      // Skip __proto__ for security
      if (key === '__proto__') {
        return;
      }

      const src = target[key];

      // Recursion prevention
      if (val === target) {
        return;
      }

      // If new value isn't a plain object, clone and assign
      if (!isPlainObject(val)) {
        target[key] = clone(val, options);
        return;
      }

      // If target property doesn't exist or isn't a plain object,
      // create new object and deep extend
      if (!isPlainObject(src)) {
        target[key] = deepExtend({}, val, options);
        return;
      }

      // Both are plain objects, extend recursively
      deepExtend(src, val, options);
    });
  });

  return target;
};

export const pick = (obj, ...keys) => {
  let copy = {};
  each(keys, function(key) {
    if (obj?.[key] !== undefined) {
      copy[key] = obj[key];
    }
  });
  return copy;
};

export const arrayFromObject = (obj) => {
  if (isArray(obj)) {
    return obj;
  }
  let arr = [];
  each(obj, (value, key) => {
    arr.push({
      value,
      key,
    });
  });
  return arr;
};

/*
  Access a nested object field from a string, like 'a.b.c'
*/
export const get = function(obj, path = '') {
  if (typeof path !== 'string') {
    return undefined;
  }

  function extractArrayLikeAccess(part) {
    const key = part.substring(0, part.indexOf('['));
    const index = parseInt(part.substring(part.indexOf('[') + 1, part.indexOf(']')), 10);
    return { key, index };
  }

  function getCombinedKey(path) {
    const dotIndex = path.indexOf('.');
    if (dotIndex !== -1) {
      const nextDotIndex = path.indexOf('.', dotIndex + 1);
      if (nextDotIndex !== -1) {
        return path.slice(0, nextDotIndex);
      }
    }
    return path;
  }

  if (obj === null || !isObject(obj)) {
    return undefined;
  }

  const parts = path.split('.');
  let currentObject = obj;

  for (let i = 0; i < parts.length; i++) {
    if (currentObject === null || !isObject(currentObject)) {
      return undefined;
    }

    let part = parts[i];

    if (part.includes('[')) {
      const { key, index } = extractArrayLikeAccess(part);

      if (key in currentObject && isArray(currentObject[key]) && index < currentObject[key].length) {
        currentObject = currentObject[key][index];
      }
      else {
        return undefined;
      }
    }
    else {
      if (part in currentObject) {
        currentObject = currentObject[part];
      }
      else {
        const remainingPath = parts.slice(i).join('.');
        if (remainingPath in currentObject) {
          currentObject = currentObject[remainingPath];
          break;
        }
        else {
          const combinedKey = getCombinedKey(`${part}.${parts[i + 1]}`);
          if (combinedKey in currentObject) {
            currentObject = currentObject[combinedKey];
            i++;
          }
          else {
            return undefined;
          }
        }
      }
    }
  }

  return currentObject;
};

/* This is useful for callbacks or other scenarios where you want to avoid the
   values of a reference object becoming stale when a source object changes
*/
export const proxyObject = (sourceObj = noop, referenceObj = {}) => {
  return new Proxy(referenceObj, {
    get: (target, property) => {
      const propKey = typeof property === 'symbol' ? property.toString() : property;
      return get(referenceObj, propKey) || get(sourceObj(), propKey);
    },
  });
};

export const onlyKeys = (obj, keysToKeep) => {
  return keysToKeep.reduce((accumulator, key) => {
    if (obj.hasOwnProperty(key)) {
      accumulator[key] = obj[key];
    }
    return accumulator;
  }, {});
};

/*
  Return true if non-inherited property
*/
export const hasProperty = (obj, prop) => {
  return Object.prototype.hasOwnProperty.call(obj, prop);
};

/*
  Reverses a lookup object
  start { a: 1, b: [1, 2] }
  end { 1: ['a', 'b'], 2: 'b' }
*/
export const reverseKeys = (obj) => {
  const newObj = {};
  const pushValue = (key, value) => {
    if (isArray(newObj[key])) {
      newObj[key].push(value);
    }
    else if (newObj[key]) {
      newObj[key] = [newObj[key], value];
    }
    else {
      newObj[key] = value;
    }
  };
  Object.keys(obj).forEach((key) => {
    if (isArray(obj[key])) {
      each(obj[key], (subKey) => {
        pushValue(subKey, key);
      });
    }
    else {
      pushValue(obj[key], key);
    }
  });
  return newObj;
};

/*
  Searches a search object
  returning matches for a query

  Matches are sorted
    - Start of word
    - Start of any word
    - Anywhere in string
*/
export const weightedObjectSearch = (query = '', objectArray = [], {
  returnMatches = false,
  matchAllWords = true,
  propertiesToMatch = [],
} = {}) => {
  if (!query) {
    return objectArray;
  }
  query = query.trim();
  query = escapeRegExp(query);
  let words = query.split(' '),
    wordRegexes = [],
    regexes = {
      startsWith: new RegExp(`^${query}`, 'i'),
      wordStartsWith: new RegExp(`\\s${query}`, 'i'),
      anywhere: new RegExp(query, 'i'),
    },
    weights = {
      startsWith: 1,
      wordStartsWith: 2,
      anywhere: 3,
      anyWord: 4,
    },
    calculateWeight = (obj) => {
      let matchDetails = [],
        weight;
      // do a weighted search across all fields
      each(propertiesToMatch, (field) => {
        let value = get(obj, field),
          fieldWeight;
        if (value) {
          each(regexes, (regex, name) => {
            if (fieldWeight) {
              return;
            }
            if (String(value).search(regex) !== -1) {
              fieldWeight = weights[name];
              if (returnMatches) {
                matchDetails.push({
                  field,
                  query,
                  name,
                  value,
                  weight: fieldWeight,
                });
              }
            }
          });
          // match any word higher score for more words
          if (!weight && wordRegexes.length) {
            let wordsMatching = 0;
            each(wordRegexes, regex => {
              if (String(value).search(regex) !== -1) {
                wordsMatching++;
              }
            });
            if (wordsMatching > 0) {
              if (!matchAllWords || (matchAllWords && wordsMatching === wordRegexes.length)) {
                fieldWeight = weights['anyWord'] / wordsMatching;
                if (returnMatches) {
                  matchDetails.push({
                    field,
                    query,
                    name: 'anyWord',
                    value,
                    matchCount: wordsMatching,
                  });
                }
              }
            }
          }
          if (fieldWeight && (!weight || fieldWeight < weight)) {
            weight = fieldWeight;
          }
        }
      });
      // flag for removal if not a match
      if (returnMatches) {
        obj.matches = matchDetails;
      }
      obj.remove = !weight;
      return weight;
    };
  if (objectArray.length == 1) {
    objectArray.push([]);
  }

  if (words.length > 1) {
    each(words, (word) => {
      wordRegexes.push(new RegExp(`(\W|^)${word}(\W|$)`, 'i'));
    });
  }

  each(objectArray, obj => {
    // clear previous remove flag and weight if present
    delete obj.remove;
    delete obj.weight;

    obj.weight = calculateWeight(obj);
  });

  let result = objectArray
    .filter(obj => !obj.remove)
    .sort((a, b) => {
      return a.weight - b.weight;
    });
  return result;
};
